import { Badge, HStack, Icon, Text } from "@chakra-ui/react"
import type { FC } from "react"
import { useMemo } from "react"

import CircleCheckEmpty from "@/ui/components/icons/CircleCheckEmpty"
import CircleDashed from "@/ui/components/icons/CircleDashed"
import CircleMinusRegular from "@/ui/components/icons/CircleMinusRegular"
import CircleXMarkRegular from "@/ui/components/icons/CircleXMarkRegular"

export type SimulationResultType =
  (typeof SimulationResult)[keyof typeof SimulationResult]

export enum SimulationResult {
  Success = "success",
  Passed = "passed",
  Failed = "failed",
  Error = "error",
  Warning = "warning",
  Low = "low",
  Medium = "medium",
  High = "high",
  Unknown = "unknown",
}

type Props = {
  result: SimulationResultType
  label?: Partial<Record<SimulationResultType, string>>
  iconOnly?: boolean
}
const SimulationResultBadge: FC<Props> = ({
  result,
  label,
  iconOnly = false,
}) => {
  const details = useMemo(() => {
    switch (result) {
      case SimulationResult.Success:
      case SimulationResult.Passed:
        return {
          backgroundColor: "green.100",
          text: label?.[result] ?? "Passed",
          textColor: "green.600",
          icon: CircleCheckEmpty,
        }
      case SimulationResult.Error:
      case SimulationResult.Failed:
        return {
          backgroundColor: "red.100",
          text: label?.[result] ?? "Failed",
          textColor: "red.600",
          icon: CircleXMarkRegular,
        }
      case SimulationResult.Unknown:
        return {
          backgroundColor: "yellow.100",
          text: label?.[result] ?? "Unknown",
          textColor: "yellow.600",
          icon: CircleMinusRegular,
        }
      case SimulationResult.Warning:
        return {
          backgroundColor: "yellow.100",
          text: label?.[result] ?? "Warning",
          icon: CircleMinusRegular,
          textColor: "yellow.600",
        }
      case SimulationResult.Low:
        return {
          backgroundColor: "yellow.100",
          text: label?.[result] ?? "Low",
          textColor: "yellow.600",
        }
      case SimulationResult.Medium:
        return {
          backgroundColor: "orange.100",
          text: label?.[result] ?? "Medium",
          textColor: "orange.600",
        }
      case SimulationResult.High:
        return {
          backgroundColor: "red.100",
          text: label?.[result] ?? "High",
          textColor: "red.600",
        }
      default:
        return {
          backgroundColor: "gray.100",
          icon: CircleDashed,
          text: "-",
          textColor: "gray.300",
        }
    }
  }, [result, label])

  return (
    <Badge
      backgroundColor={details.backgroundColor}
      borderRadius={iconOnly ? "xl" : "md"}
      textTransform="none"
    >
      {iconOnly ? (
        <HStack align="center" p={1} spacing={1}>
          <Icon as={details.icon} fill={details.textColor} h={4} w={4} />
        </HStack>
      ) : (
        <HStack align="center" p={1} spacing={1}>
          <Icon as={details.icon} fill={details.textColor} h={4} w={4} />
          <Text color={details.textColor} fontSize="sm">
            {details.text}
          </Text>
        </HStack>
      )}
    </Badge>
  )
}

export default SimulationResultBadge
